import env from '../env';
import type { ApiNotification } from '../graphql/query/notification.queries';
import type { NotificationType, UserNotification } from '../../types/notification';
import { inferMediaTypeFromMessage } from './notificationNavigation';

type NotificationInfo = Record<string, unknown>;

const KNOWN_TYPES = new Set<string>([
  'LIKE',
  'MEDIA_APPROVED',
  'MEDIA_REJECTED',
  'UPLOAD_REQUEST',
  'SUBSCRIPTION_EXPIRED',
  'CONTENT_STREAMING',
  'CONTINUE_WATCHING',
  'INVOICE',
  'RELEASE',
  'GENERAL',
]);

const TYPE_ALIASES: Record<string, NotificationType> = {
  LIKED: 'LIKE',
  MEDIA_LIKE: 'LIKE',
  LIKE_NOTIFICATION: 'LIKE',
  APPROVED: 'MEDIA_APPROVED',
  MEDIA_APPROVAL: 'MEDIA_APPROVED',
  REJECTED: 'MEDIA_REJECTED',
  MEDIA_REJECTION: 'MEDIA_REJECTED',
  UPLOAD: 'UPLOAD_REQUEST',
  MODERATION_REQUEST: 'UPLOAD_REQUEST',
  CONTENT_UPLOAD_REQUEST: 'UPLOAD_REQUEST',
  MEDIA_UPLOAD_REQUEST: 'UPLOAD_REQUEST',
  PENDING_MODERATION: 'UPLOAD_REQUEST',
  MODERATION: 'UPLOAD_REQUEST',
};

const normalizeType = (nType: string): NotificationType => {
  if (!nType) { return 'GENERAL'; }

  const normalized = nType
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

  const resolved = TYPE_ALIASES[normalized] ?? normalized;
  return (KNOWN_TYPES.has(resolved) ? resolved : 'GENERAL');
};

const pickString = (info: NotificationInfo, keys: string[]): string | undefined => {
  const entries = Object.entries(info);

  for (const key of keys) {
    const direct = info[key];
    if (typeof direct === 'string' && direct.trim()) {
      return direct.trim();
    }
    if (typeof direct === 'number' && Number.isFinite(direct)) {
      return String(direct);
    }

    const match = entries.find(([entryKey]) => entryKey.toLowerCase() === key.toLowerCase());
    if (match) {
      const value = match[1];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }
  }

  return undefined;
};

const parseNotificationInfo = (nInfo?: ApiNotification['nInfo']): NotificationInfo => {
  if (!nInfo) { return {}; }

  if (typeof nInfo === 'object') {
    return nInfo as NotificationInfo;
  }

  try {
    let parsed: unknown = JSON.parse(nInfo);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return typeof parsed === 'object' && parsed !== null ? (parsed as NotificationInfo) : {};
  } catch {
    return {};
  }
};

const resolveThumbnailUrl = (url?: string | null): string | undefined => {
  if (!url) { return undefined; }
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    return url;
  }
  return `${env.APP_BASE_URL || ''}${url}`;
};

const pickFromNested = (info: NotificationInfo, keys: string[], nestedKeys: readonly string[]): string | undefined =>
  nestedKeys.reduce<string | undefined>((found, nestedKey) => {
    if (found) { return found; }
    const nested = info[nestedKey];
    if (!nested || typeof nested !== 'object') { return undefined; }
    return pickString(nested as NotificationInfo, keys);
  }, undefined);

export const mapApiNotification = (notification: ApiNotification): UserNotification => {
  const info = parseNotificationInfo(notification.nInfo);

  const reelIdKeys = [
    'reelId',
    'maId',
    'ma_id',
    'reelMaId',
    'reel_ma_id',
    'mediaApprovalId',
    'media_approval_id',
    'likedReelId',
    'liked_reel_id',
  ];

  const mediaIdKeys = [
    'jwMediaId',
    'jw_media_id',
    'maJwMediaId',
    'ma_jw_media_id',
    'likedMediaId',
    'liked_media_id',
    'mediaId',
    'media_id',
  ];

  const nestedObjectKeys = ['media', 'reel', 'likedReel', 'likedMedia', 'approval', 'data'] as const;

  const mediaId = pickString(info, mediaIdKeys) || pickFromNested(info, [...mediaIdKeys, 'id'], nestedObjectKeys);

  const reelId = pickString(info, reelIdKeys) || pickFromNested(info, reelIdKeys, nestedObjectKeys);

  const rejectionReason = pickString(info, ['rejectionReason', 'maRejectionReason', 'rejection_reason', 'ma_rejection_reason']);
  const contentAvailable = info.contentAvailable !== false && info.content_available !== false;

  const rawMediaType = pickString(info, ['maType', 'ma_type', 'mediaType', 'media_type', 'contentType', 'content_type']);
  const normalizedMediaType =
    rawMediaType?.toUpperCase() === 'VIDEOS' || rawMediaType?.toUpperCase() === 'VIDEO'
      ? 'VIDEOS'
      : rawMediaType?.toUpperCase() === 'REELS' || rawMediaType?.toUpperCase() === 'REEL'
        ? 'REELS'
        : undefined;

  const mediaType = normalizedMediaType ?? inferMediaTypeFromMessage(notification.nMessage);

  return {
    id: notification.nId,
    userId: notification.nUserId,
    type: normalizeType(notification.nType),
    message: notification.nMessage,
    thumbnailUrl: resolveThumbnailUrl(notification.nThumbnailUrl),
    createdAt: notification.nCreatedAt,
    isRead: notification.nIsRead,
    reelId,
    mediaId,
    mediaType,
    rejectionReason,
    contentAvailable,
  };
};
